import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface BreadcrumbItem {
  label: string;
  url: string;
  isLast: boolean;
}

const LABEL_MAP: Record<string, string> = {
  'dashboard': 'Dashboard',
  'ai-insights': 'AI Insights',
  'contests': 'Campaigns',
  'add-contests': 'Add Campaign',
  'contest-details': 'Campaign Details',
  'election-rules': 'Draw Rules',
  'draw-contest-details': 'Draw Details',
  'prize': 'Prizes',
  'add-prize': 'Add Prize',
  'winner': 'Winners',
  'participate-details': 'Participants',
  'contest-monitoring': 'Monitoring',
  'quiz': 'Quiz',
  'add-quiz': 'Add Quiz',
  'users': 'Users',
  'client-users': 'Client Users',
  'add-client': 'Add Client',
  'track-users': 'Track Users',
  'engagement-users': 'Engagement',
  'engagement-details': 'Engagement Details',
  'clients': 'Clients',
  'client-details': 'Client Details',
  'client-subusers': 'Sub Users',
  'categories': 'Categories',
  'add-categories': 'Add Category',
  'subscription': 'Subscription',
  'add-subscription': 'Add Plan',
  'client-subscription': 'Client Subscriptions',
  'client-subscription-history': 'History',
  'override-plan': 'Override Plan',
  'discount': 'Discounts',
  'feedback': 'Feedback',
  'feedback-details': 'Feedback Details',
  'feedback-support': 'Support',
  'notification': 'Notifications',
  'create': 'Create',
  'create-custom': 'Create Custom',
  'setting': 'Settings',
  'policy-details': 'Policy Details',
  'add-subuser': 'Add Sub User',
  'add-policy': 'Add Policy',
  'pages': 'Pages',
  'add-page': 'Add Page',
  'referral-config': 'Referral Config',
  'add': 'Add',
  'edit': 'Edit',
  'reward-history': 'Reward History',
  'activity-log': 'Activity Log',
  'task-management': 'Task Management',
  'add-task': 'Add Task',
  'add-task-rule': 'Add Task Rule',
  'add-questionnaire': 'Add Questionnaire',
  'questionnaire-responses': 'Questionnaire Responses',
  'analysis-reports': 'Analysis Reports',
  'subscription-history': 'Subscription History',
  'activity-logs': 'Activity Logs',
  'super-admin-logs': 'Super Admin Logs',
  'ads': 'Ads',
  'add-ads': 'Add Ad',
  'sectors': 'Sectors',
  'add-sector': 'Add Sector',
  'business-subject': 'Business Objective',
  'business-objective': 'Business Objective',
  'client-qr': 'Client QR',
  'add-client-qr': 'Add QR',
  'client-branches': 'Branches',
  'add-client-branch': 'Add Branch',
  'interests': 'Interests',
  'add-interest': 'Add Interest',
  'newsletter': 'Newsletter',
  'email-templates': 'Email Templates',
  'add-email-template': 'Add Template',
  'terms-condition': 'Terms & Conditions',
  'add-terms-condition': 'Add Terms',
  'request': 'Requests',
  'profile': 'Profile',
  'reset-password': 'Reset Password',
};

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private breadcrumbs$ = new BehaviorSubject<BreadcrumbItem[]>([]);
  breadcrumbs = this.breadcrumbs$.asObservable();

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.breadcrumbs$.next(this.build(e.urlAfterRedirects || e.url));
      });
  }

  private build(url: string): BreadcrumbItem[] {
    // Strip query params
    const path = url.split('?')[0];
    const segments = path.split('/').filter(Boolean);

    if (!segments.length || segments[0] === 'auth') return [];

    const items: BreadcrumbItem[] = [];
    let cumulative = '';

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      // Skip numeric IDs — just label the parent as "Edit"
      if (/^\d+$/.test(seg)) {
        if (items.length) items[items.length - 1].label = 'Edit';
        continue;
      }
      cumulative += '/' + seg;
      const label = LABEL_MAP[seg] || this.humanize(seg);
      items.push({ label, url: cumulative, isLast: false });
    }

    if (items.length) items[items.length - 1].isLast = true;
    return items;
  }

  private humanize(seg: string): string {
    return seg
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
}
